/**
 * 全局初始化状态管理
 * 负责用户信息获取、权限验证等初始化逻辑
 */

import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { history } from '@umijs/max';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import defaultSettings from '../../config/defaultSettings';
import { isRouteNeedLogin } from './auth';

// 不需要登录的页面路径
const PUBLIC_PATHS = ['/user/login', '/user/register', '/user/register-result'];

/**
 * 初始状态类型定义
 */
export interface InitialState {
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}

/**
 * 获取用户信息
 */
async function fetchUserInfo(): Promise<API.CurrentUser | undefined> {
  try {
    const msg = await queryCurrentUser({
      skipErrorHandler: true,
    });
    return msg.data;
  } catch (_error) {
    // 获取用户信息失败，跳转到登录页
    history.push('/user/login');
    return undefined;
  }
}

/**
 * 全局初始化函数
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 */
export async function getInitialState(): Promise<InitialState> {
  const { location } = history;
  const needLogin = isRouteNeedLogin(location.pathname);

  // 如果不是公开页面且需要登录，获取用户信息
  if (!PUBLIC_PATHS.includes(location.pathname) && needLogin) {
    const currentUser = await fetchUserInfo();

    // 根据用户信息动态设置 title 和 logo
    const settings = {
      ...defaultSettings,
      title: currentUser?.tenantName || defaultSettings.title,
      logo: currentUser?.logo || defaultSettings.logo,
    } as Partial<LayoutSettings>;

    return {
      fetchUserInfo,
      currentUser,
      settings,
    };
  }

  // 公开页面或不需要登录的页面
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}
